import type { BoardFilter } from "~/features/board/types";
import type { GalleryKind, GalleryStatus } from "~/features/gallery/types";
import type { ChatTab } from "~/features/messages/types";
import type { SearchFilters } from "~/features/search/types";

export const queryKeys = {
  call: {
    all: ["call"] as const,
    incoming: () => [...queryKeys.call.all, "incoming"] as const,
    lovenseMenu: (partnerId: string) =>
      [...queryKeys.call.all, "lovenseMenu", partnerId] as const,
    uncompletedLog: (partnerId: string) =>
      [...queryKeys.call.all, "uncompletedLog", partnerId] as const,
  },
  auth: {
    all: ["auth"] as const,
    passwordResetAuthCode: (authCode: string) =>
      [...queryKeys.auth.all, "passwordResetAuthCode", authCode] as const,
    lovenseLiveRecording: () =>
      [...queryKeys.auth.all, "lovenseLiveRecording"] as const,
  },
  user: {
    all: ["user"] as const,
    me: () => [...queryKeys.user.all, "me"] as const,
    profile: (userId: string) =>
      [...queryKeys.user.all, "profile", userId] as const,
    publicImages: (userId: string) =>
      [...queryKeys.user.all, "publicImages", userId] as const,
    point: () => [...queryKeys.user.all, "point"] as const,
    hasPurchased: () => [...queryKeys.user.all, "hasPurchased"] as const,
    profileMedia: (userId: string) =>
      [...queryKeys.user.all, "profileMedia", userId] as const,
  },
  discover: {
    all: ["discover"] as const,
    list: () => [...queryKeys.discover.all, "list"] as const,
    recentVideoCallUsers: () =>
      [...queryKeys.discover.all, "recentVideoCallUsers"] as const,
    highReviewScoreUsers: () =>
      [...queryKeys.discover.all, "highReviewScoreUsers"] as const,
    preferenceMatchUsers: () =>
      [...queryKeys.discover.all, "preferenceMatchUsers"] as const,
    talkedBeforeUsers: () =>
      [...queryKeys.discover.all, "talkedBeforeUsers"] as const,
    newcomers: () => [...queryKeys.discover.all, "newcomers"] as const,
    recommendRanking: () =>
      [...queryKeys.discover.all, "recommendRanking"] as const,
  },
  search: {
    all: ["search"] as const,
    lists: () => [...queryKeys.search.all, "list"] as const,
    list: (filters: SearchFilters) =>
      [...queryKeys.search.lists(), filters] as const,
  },
  chat: {
    all: ["chat"] as const,
    conversations: (tab?: ChatTab) =>
      tab !== undefined
        ? ([...queryKeys.chat.all, "conversations", tab] as const)
        : ([...queryKeys.chat.all, "conversations"] as const),
    messages: (frdId: string) =>
      [...queryKeys.chat.all, "messages", frdId] as const,
    openedImages: () => [...queryKeys.chat.all, "openedImages"] as const,
    openedVideos: () => [...queryKeys.chat.all, "openedVideos"] as const,
    openedAudios: () => [...queryKeys.chat.all, "openedAudios"] as const,
    totalUnread: () => [...queryKeys.chat.all, "totalUnread"] as const,
  },
  userPreferences: {
    all: ["userPreferences"] as const,
    detail: () => [...queryKeys.userPreferences.all, "detail"] as const,
  },
  myProfile: {
    all: ["myProfile"] as const,
    detail: () => [...queryKeys.myProfile.all, "detail"] as const,
  },
  moderation: {
    all: ["moderation"] as const,
    blockList: () => [...queryKeys.moderation.all, "blockList"] as const,
  },
  notifications: {
    all: ["notifications"] as const,
    settings: () => [...queryKeys.notifications.all, "settings"] as const,
  },
  board: {
    all: ["board"] as const,
    lists: () => [...queryKeys.board.all, "list"] as const,
    list: (filter: BoardFilter) =>
      [...queryKeys.board.lists(), filter] as const,
  },
  dailyBonus: {
    all: ["dailyBonus"] as const,
    /** 受取（utage_web_daily_bonus）＋残高＋購入コース可否を 1 フェッチで集約 */
    data: () => [...queryKeys.dailyBonus.all, "data"] as const,
  },
  fukadaCampaign: {
    all: ["fukadaCampaign"] as const,
    schedule: () => [...queryKeys.fukadaCampaign.all, "schedule"] as const,
    broadcasterLive: () =>
      [...queryKeys.fukadaCampaign.all, "broadcasterLive"] as const,
    pointBackProgress: () =>
      [...queryKeys.fukadaCampaign.all, "pointBackProgress"] as const,
    lotteryInfo: () =>
      [...queryKeys.fukadaCampaign.all, "lotteryInfo"] as const,
    consumePointRanking: () =>
      [...queryKeys.fukadaCampaign.all, "consumePointRanking"] as const,
    liveRecording: () =>
      [...queryKeys.fukadaCampaign.all, "liveRecording"] as const,
  },
  review: {
    all: ["review"] as const,
    list: (userId: string) =>
      [...queryKeys.review.all, "list", userId] as const,
    postingEnabled: (targetUserId: string) =>
      [...queryKeys.review.all, "postingEnabled", targetUserId] as const,
    badgeGrantStatus: (targetUserId: string) =>
      [...queryKeys.review.all, "badgeGrantStatus", targetUserId] as const,
    badgeProgress: (targetUserId: string) =>
      [...queryKeys.review.all, "badgeProgress", targetUserId] as const,
  },
  lovense: {
    all: ["lovense"] as const,
    menu: (callType: "live" | "video", partnerId: string) =>
      [...queryKeys.lovense.all, "menu", callType, partnerId] as const,
  },
  present: {
    all: ["present"] as const,
    menu: (callType: "live" | "video", partnerId: string) =>
      [...queryKeys.present.all, "menu", callType, partnerId] as const,
  },
  live: {
    all: ["live"] as const,
    channels: () => [...queryKeys.live.all, "channels"] as const,
    lovenseMenu: (broadcasterId: string) =>
      [...queryKeys.live.all, "lovenseMenu", broadcasterId] as const,
    uncompletedLog: (broadcasterId: string) =>
      [...queryKeys.live.all, "uncompletedLog", broadcasterId] as const,
    presentMenuSummary: (channelId: string) =>
      [...queryKeys.live.all, "presentMenuSummary", channelId] as const,
    ngWordList: () => [...queryKeys.live.all, "ngWordList"] as const,
    superChatMenu: (partnerId: string) =>
      [...queryKeys.live.all, "superChatMenu", partnerId] as const,
    livePollMenu: (partnerId: string) =>
      [...queryKeys.live.all, "livePollMenu", partnerId] as const,
    livePoll: (partnerId: string) =>
      [...queryKeys.live.all, "livePoll", partnerId] as const,
  },
  creditPurchaseCourse: {
    all: ["creditPurchaseCourse"] as const,
    info: () => [...queryKeys.creditPurchaseCourse.all, "info"] as const,
  },
  gallery: {
    all: ["gallery"] as const,
    items: (kind: GalleryKind, status: GalleryStatus) =>
      [...queryKeys.gallery.all, "items", kind, status] as const,
  },
  footprint: {
    all: ["footprint"] as const,
    list: () => [...queryKeys.footprint.all, "list"] as const,
  },
  bookmark: {
    all: ["bookmark"] as const,
    list: () => [...queryKeys.bookmark.all, "list"] as const,
  },
  like: {
    all: ["like"] as const,
    received: () => [...queryKeys.like.all, "received"] as const,
    sent: () => [...queryKeys.like.all, "sent"] as const,
  },
  demoCall: {
    all: ["demoCall"] as const,
    info: () => [...queryKeys.demoCall.all, "info"] as const,
  },
  fleamarket: {
    all: ["fleamarket"] as const,
    items: () => [...queryKeys.fleamarket.all, "items"] as const,
    itemsList: (params: Record<string, unknown>) =>
      [...queryKeys.fleamarket.items(), params] as const,
    itemDetail: (itemId: string) =>
      [...queryKeys.fleamarket.all, "itemDetail", itemId] as const,
    favorites: (userId: string) =>
      [...queryKeys.fleamarket.all, "favorites", userId] as const,
    transactions: (userId: string) =>
      [...queryKeys.fleamarket.all, "transactions", userId] as const,
    transactionDetail: (transactionId: string) =>
      [
        ...queryKeys.fleamarket.all,
        "transactionDetail",
        transactionId,
      ] as const,
    shipmentDetail: (userId: string, transactionId: string) =>
      [
        ...queryKeys.fleamarket.all,
        "shipmentDetail",
        userId,
        transactionId,
      ] as const,
    yamatoDeliveryStatus: (userId: string, transactionId: string) =>
      [
        ...queryKeys.fleamarket.all,
        "yamatoDeliveryStatus",
        userId,
        transactionId,
      ] as const,
    address: (userId: string) =>
      [...queryKeys.fleamarket.all, "address", userId] as const,
    seller: (sellerId: string) =>
      [...queryKeys.fleamarket.all, "seller", sellerId] as const,
  },
} as const;
